import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import { ReactComponent as Back } from "../../../assets/svgs/back-button.svg";
import { getApi } from "../../../api/api_instance";
import SearchBar from "../../../components/Elements/Search/SearchBar";
import SectionList from "./components/SectionList";
import SubsectionList from "./components/SubSectionList";
import TextStyles from "../../../components/Elements/TextStyles/TextStyles";
import QuestionList from "./components/QuestionList";

const scrollableContainer = {
  maxHeight: "1000px",
  overflowY: "auto",
  padding: "10px",
  borderRadius: "8px"
};

const ViewForm = ({
  formId,
  clientId,
  sectionurl,
  subsectionurl,
  questionurl
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState("-createdAt");
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [subsections, setSubsections] = useState([]);
  const [selectedSubsection, setSelectedSubsection] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [totalQuestions, setTotalQuestions] = useState(0);
  const pageSize = 20;
  const navigate = useNavigate();

  useEffect(() => {
    fetchSections();
  }, []);
  const getDataBlock = (data) => {
    return data.standard || data.custom || {};
  };

  const fetchSections = async () => {
    setLoading(true);
    try {
      const response = await getApi({
        apiUrl: sectionurl(formId, currentPage, pageSize, sortOrder),
        headers: { client: clientId }
      });
      if (response.success) {
        const { custom = {}, standard = {} } = response.data;

        // Add type to each section so we know where it came from
        const customSections = (custom.sections ?? []).map((s) => ({
          ...s,
          type: "custom"
        }));

        const standardSections = (standard.sections ?? []).map((s) => ({
          ...s,
          type: "standard"
        }));

        const combinedSections = [...customSections, ...standardSections];

        setSections(combinedSections);

        if (combinedSections.length > 0) {
          const firstSection = combinedSections[0];
          setSelectedSection(firstSection.sectionId);
          fetchSubsections(firstSection.sectionId, firstSection.type); // pass type
        }
      } else {
        console.error("Failed to fetch sections");
      }
    } catch (error) {
      console.error("Error fetching sections", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubsections = async (sectionId) => {
    setLoading(true);
    try {
      const response = await getApi({
        apiUrl: subsectionurl(formId, sectionId),
        headers: { client: clientId }
      });

      if (response.success) {
        const dataBlock = getDataBlock(response.data);
        const subsectionList = dataBlock.subsections ?? [];
        setSubsections(subsectionList);
        setSelectedSection(sectionId);

        if (subsectionList.length > 0) {
          const firstSubsection = subsectionList[0];
          setSelectedSubsection(firstSubsection.subsectionId);
          fetchQuestions(firstSubsection.subsectionId, sectionId);
        }
      } else {
        console.error("Failed to fetch subsections");
      }
    } catch (error) {
      console.error("Error fetching subsections", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = useCallback(
    async (subsectionId = selectedSubsection, sectionId = selectedSection) => {
      if (!subsectionId || !sectionId) return;

      setLoading(true);
      try {
        const response = await getApi({
          apiUrl: questionurl(formId, sectionId, subsectionId, searchTerm),
          headers: { client: clientId }
        });

        if (response.success) {
          const rawQuestions = response.data.questions ?? [];

          const normalizedQuestions = rawQuestions.map((q) => {
            if (q.questionType === "standard" && Array.isArray(q.questions)) {
              return {
                id: q.id || Math.random().toString(),
                title: q.title ?? null,
                questions: q.questions
              };
            } else {
              return {
                id: q._id || q.id || Math.random().toString(),
                title: null,
                questions: [q]
              };
            }
          });

          setQuestions(normalizedQuestions);
          setTotalQuestions(response.data.totalQuestions ?? 0);
        } else {
          setQuestions([]);
          setTotalQuestions(0);
        }
      } catch (error) {
        console.error("Error fetching questions", error);
        message.error("Failed to fetch questions");
      } finally {
        setLoading(false);
      }
    },
    [searchTerm]
  );

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  return (
    <div className="h-full px-5 pb-5 bg-white ">
      <div className="flex justify-end text-xs font-light p-3">
        <p>Home</p>
        <span>/</span>
        <p>Dynamic Form</p>
        <span>/</span>
        <TextStyles color="gradient">All Questions</TextStyles>
      </div>
      <div className="flex justify-between pb-2">
        <div className="flex items-center">
          <Back className="cursor-pointer" onClick={() => navigate(-1)} />
          <h3 className="text-[#06513D] text-xl">ALL QUESTIONS</h3>
        </div>
      </div>

      <div className="w-full border border-[#3BA2B959] rounded-[10px] pt-5 shadow-[0_0_16px_0_#3BA2B91F] mb-6">
        <SectionList
          sections={sections}
          selectedSection={selectedSection}
          onSectionSelect={fetchSubsections}
        />

        <hr className="border-t-[1px] border-[#3BA2B959] shadow-[0_0_16px_0_#3BA2B91F]" />

        {selectedSection && (
          <SubsectionList
            subsections={subsections}
            selectedSubsection={selectedSubsection}
            onSubsectionSelect={(subsectionId) => {
              setSelectedSubsection(subsectionId);
              fetchQuestions(subsectionId, selectedSection);
            }}
          />
        )}
      </div>

      <div className="w-full bg-white border border-[#3BA2B959] rounded-[10px] pt-5 shadow-[0_0_16px_0_#3BA2B91F] mb-6">
        <div className="">
          <div className="flex items-center gap-5 ml-10 mb-6">
            <div className="px-8 h-[42px] flex items-center justify-center border border-[#06513D] bg-[#B6D4D933] rounded-md text-[#06513D] text-base font-light">
              TOTAL QUESTION: {totalQuestions}
            </div>

            <SearchBar
              width="300px"
              onSearch={(value) => setSearchTerm(value)}
            />
          </div>
          <div style={scrollableContainer}>
            <QuestionList
              loading={loading}
              questions={questions}
              selectedSubsection={selectedSubsection}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewForm;
